import React, { useState, useEffect, useContext } from "react";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";
import { format, startOfDay, addDays } from "date-fns";
import { LocationContext } from "./LocationContext";
import { UMSSBlue } from "../utils/colors";

function TimeSlots({ onSelect, selectedTime, selectedDate }) {
  // Always call hooks unconditionally
  const { selectedLocation } = useContext(LocationContext);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const db = getFirestore();

  useEffect(() => {
    // Reset available slots when date is cleared
    if (!selectedDate) {
      setAvailableSlots([]);
      return;
    }
    
    if (selectedDate && selectedLocation) {
      console.log("Fetching time slots for date:", selectedDate, "and location:", selectedLocation);
      setLoading(true);
      const dayStart = startOfDay(selectedDate);
      const dayEnd = addDays(dayStart, 1);
      console.log("Day start:", dayStart, "Day end:", dayEnd);
      const slotsRef = collection(db, "offices", selectedLocation.id, "appointments");
      const q = query(
        slotsRef,
        where("booked", "==", false),
        where("canceled", "==", false),  // IMPORTANT: Exclude canceled appointments
        where("dateTime", ">=", dayStart),
        where("dateTime", "<", dayEnd)
      );
      console.log("Query created:", q);
      getDocs(q)
        .then((querySnapshot) => {
          console.log("Query snapshot received:", querySnapshot);
          const slots = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            console.log("Document data:", data);
            return {
              id: doc.id,
              dateTime: data.dateTime.toDate(),
            };
          });
          
          // Create a map to store unique time slots
          const uniqueTimeSlots = new Map();
          
          // Process each slot
          slots.forEach(slot => {
            const timeStr = format(slot.dateTime, "h:mm a");
            
            // If this time doesn't exist yet or if we want to prioritize by some criterion
            // (here we're using the first slot we encounter for each time)
            if (!uniqueTimeSlots.has(timeStr)) {
              uniqueTimeSlots.set(timeStr, slot);
            }
          });
          
          // Convert map back to array
          const uniqueSlots = Array.from(uniqueTimeSlots.values());
          console.log("Unique slots:", uniqueSlots);
          
          // Sort the unique slots by time
          uniqueSlots.sort((a, b) => a.dateTime - b.dateTime);
          setAvailableSlots(uniqueSlots);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching time slots:", error);
          setLoading(false);
        });
    }
  }, [selectedDate, selectedLocation, db]);

  // Strengthen the conditional rendering - don't render if no date or if date is invalid
  if (!selectedDate || !(selectedDate instanceof Date) || isNaN(selectedDate.getTime())) {
    return null;
  }

  return (
    <div style={{ width: "fit-content", marginTop: "1rem", animation: "fadeIn 0.5s ease-in" }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      <h4 style={{ fontFamily: "Quicksand, sans-serif", fontSize: "18px", marginBottom: "0.5rem" }}>
        Available Times:
      </h4>
      {loading && <p>Loading available times...</p>}
      {!loading && availableSlots.length === 0 && <p>No available times.</p>}
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "8rem" }}>
        {availableSlots.map((slot) => {
          const timeStr = format(slot.dateTime, "h:mm a");
          const isSelected = selectedTime === timeStr;
          return (
            <button
              key={slot.id}
              // Log and pass both time string and appointment id
              onClick={() => {
                console.log("Appointment ID:", slot.id);
                onSelect({ time: timeStr, id: slot.id });
              }}
              style={{
                backgroundColor: isSelected ? UMSSBlue : "#eee",
                color: isSelected ? "white" : "black",
                padding: "0.75rem 1rem",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontWeight: "500",
                fontFamily: "Quicksand, sans-serif"
              }}
            >
              {timeStr}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default TimeSlots;